import React, { useEffect, useState } from 'react';
import { GenericDialog } from '../../../components/generic/GenericDialog';
import { Grid2 as Grid } from '@mui/material';
import { Radio, RadioGroup, Stack, Typography, FormControlLabel } from '@mui/material';
import { ALL, CREATOR_ROLE, INITIAL_GET_PARAMS, SELECTED } from '../../../enums/generalEnums';
import { FormAutoComplete, FormAutoCompleteUsers, FormTextField } from '../../../components/generic/GenericTextFields';
import { BROADCAST_MESSAGE_TYPES } from '../../../enums/broadcastMessageEnums';
import { useFormik } from 'formik';
import { NewBroadcastMessageButtons } from './NewBroadcastMessageButtons';
import { createBroadcastMessage, getUsers } from '../../../config/apisauce';
import useNotiStack from '../../../hooks/useNotiStack';
import useStore from '../../../states/store';
import { ACTIVE} from '../../../enums/statusEnums';
import 'react-quill/dist/quill.snow.css';
import RichTextField from '../../../components/generic/RichTextField';

export const NewBroadcastMessageForm = ({ openDialog, creatorDetails, handleCloseDialog, onMessageCreated }) => {
  const { success } = useNotiStack();
  const [loading, setLoading] = useState(false);
  const [, { openLoader, closeLoader }] = useStore();
  const [creators, setCreators] = useState([]);
  const [creatorSearchText, setCreatorSearchText] = useState('');

  const fetchCreatorList = async (searchTerm) => {
    openLoader();
    const params = {
      ...INITIAL_GET_PARAMS,
      status: [ACTIVE],
      role: [CREATOR_ROLE],
      search: searchTerm,
      paginated: 0
    };
    const response = await getUsers(params);
    if (response?.ok) {
      setCreators(response?.data || []);
    }
    closeLoader();
  };

  const handleCancel = () => {
    formik.resetForm();
    handleCloseDialog();
  };

  const handleClearSelectedCreators = () => {
    formik.setFieldValue("selectedCreators", []);
  };

  const handleRecipientChange = (event) => {
    const value = event?.target?.value;
    formik.setFieldValue("recipient", value);

    if (value === ALL) {
      handleClearSelectedCreators();
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    const params = {
      recipients: values?.recipient === SELECTED && values?.selectedCreators?.length > 0
        ? values?.selectedCreators?.map((creator) => creator?.id)
        : ALL,
      title: values?.title,
      message: values?.message,
      type: values?.type,
      created_by: creatorDetails?.user?.id,
    };
    const response = await createBroadcastMessage(params);
    if (response.ok) {
      success('Broadcast message sent successfully!');
      handleClearSelectedCreators();
      handleCloseDialog();
      if (typeof onMessageCreated === 'function') {
        onMessageCreated();
      }

      formik.resetForm();
    }
    setLoading(false);
  };

  const formik = useFormik({
    initialValues: {
      recipient: ALL,
      title: '',
      message: '',
      type: '',
      selectedCreators: [],
    },
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    fetchCreatorList(creatorSearchText);
  }, [creatorSearchText]);

  return (
    <GenericDialog
      title={'New Creator Broadcast Message'}
      openDialog={openDialog}
      handleClose={() => handleCloseDialog()}
      width={'850px'}
    >
      <Grid container mt={2} spacing={2}>
        <Grid item>
          <Typography>
            Reach all Collab Creators with an important update, announcement, or opportunity.
            Your message will be delivered directly to their inbox and appear in the notifications panel.
          </Typography>
        </Grid>
        <Grid item size={{ xs: 12 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
            <Typography fontWeight={700}>Recipient:</Typography>
            <RadioGroup
              row
              name="recipient"
              value={formik.values.recipient}
              onChange={handleRecipientChange}
            >
              <FormControlLabel value={SELECTED} control={<Radio />} label="Selected Creators" />
              <FormControlLabel value={ALL} control={<Radio />} label="All Creators" />
            </RadioGroup>
          </Stack>
        </Grid>
        {formik.values.recipient === SELECTED && (
          <Grid item size={{ xs: 12 }} mt={-3}>
            <FormAutoCompleteUsers
              multiple
              value={formik.values.selectedCreators}
              options={creators
                ?.filter((creator) => !formik.values.selectedCreators.some((selected) => selected.id === creator.id))
                ?.map((creator) => ({
                  id: creator.id,
                  name: creator.name,
                  email: creator.email,
                  profilePicture: creator?.details?.profile_picture?.location,
                }))
              }
              inputValue={creatorSearchText}
              onInputChange={(event, newInputValue) => {
                setCreatorSearchText(newInputValue);
              }}
              popupIcon={null}
              isRequired={false}
              handleChange={(value) => formik.setFieldValue('selectedCreators', value)}
            />
          </Grid>
        )}
        <Grid item size={{ xs: 12 }}>
          <FormTextField
            label={'Title'}
            name="title"
            value={formik.values.title}
            isRequired={false}
            color="black"
            fullWidth
            handleChange={(value) => formik.setFieldValue('title', value)}
          />
        </Grid>
        <Grid item size={{ xs: 12 }}>
          <RichTextField value={formik.values.message} label="Message" onChange={(value) => formik.setFieldValue('message', value)} />
        </Grid>
        <Grid item size={{ xs: 12 }}>
          <FormAutoComplete
            value={formik.values.type}
            options={BROADCAST_MESSAGE_TYPES}
            label={'Type'}
            placeholder={'Please Select'}
            error={Boolean(formik.errors.type)}
            helperText={formik.errors.type}
            isRequired={false}
            color='black'
            handleChange={(value) => formik.setFieldValue('type', value)}
          />
        </Grid>
        <Grid item size={{ xs: 12 }} mt={-2}>
          <NewBroadcastMessageButtons
            handleCancel={handleCancel}
            handleSubmit={formik.handleSubmit}
          />
        </Grid>
      </Grid>
    </GenericDialog>
  );
};